import React, { useEffect, useMemo, useState } from 'react';
import { cx } from '@/utils';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  MinusIcon,
  PlusIcon,
  TargetIcon,
} from 'lucide-react';
import { ComposableMap, Geographies, Geography, GeographyProps } from 'react-simple-maps';

import { MapType, PageMapBlock } from '@/components/common/Providers/GraphqlProvider/generated';

import { mapDescriptors } from '../admin/hooks/useMapElements/index';
import { CurrentMapElement } from '../admin/schema';
import InfoCard from './components/InfoCard';
import useAutoFitGeoJson from './hooks/useAutoFitGeoJson';

const COLORS = {
  HIDDEN: '#F6F8F98F',
  DEFAULT_ACCENT: '#068460',
  DEFAULT_NEUTRAL: '#CDE6DF',
  WHITE: '#FFFFFF',
};

type TooltipState = {
  name: string;
  x: number;
  y: number;
};

const getGeoStyles = (highlight: boolean, geoConfig?: CurrentMapElement) => {
  if (!highlight) {
    return {
      default: {
        fill: COLORS.WHITE,
        stroke: COLORS.HIDDEN,
        strokeWidth: 0.5,
        outline: 'none',
      },
      hover: {
        fill: COLORS.WHITE,
        stroke: COLORS.HIDDEN,
        strokeWidth: 0.5,
        outline: 'none',
        cursor: 'default',
      },
      pressed: {
        fill: COLORS.WHITE,
        stroke: COLORS.HIDDEN,
        strokeWidth: 0.75,
        outline: 'none',
      },
    };
  }

  const _default = {
    fill: geoConfig?.color ? `${geoConfig.color}40` : COLORS.DEFAULT_NEUTRAL,
    stroke: COLORS.WHITE,
    strokeWidth: 0.5,
    outline: 'none',
  };

  const [hover, pressed] =
    geoConfig?.extra || geoConfig?.url
      ? [
          {
            fill: geoConfig?.color ? `${geoConfig.color}DD` : `${COLORS.DEFAULT_ACCENT}DD`,
            stroke: COLORS.WHITE,
            strokeWidth: highlight ? 1 : 0.5,
            outline: 'none',
            cursor: highlight ? 'pointer' : 'default',
            boxShadow: '0 20px 20px -5px rgb(0 0 0 / 0.1)',
          },
          {
            fill: geoConfig?.color || COLORS.DEFAULT_ACCENT,
            stroke: COLORS.WHITE,
            strokeWidth: 0.75,
            outline: 'none',
          },
        ]
      : [_default, _default];

  return {
    default: _default,
    hover,
    pressed,
  };
};

const MapBlockPresentation = ({ currentPageMapBlock }: { currentPageMapBlock: PageMapBlock }) => {
  const elements = useMemo(() => {
    if (currentPageMapBlock.elementsConfig) return JSON.parse(currentPageMapBlock.elementsConfig);
    return [];
  }, []) as CurrentMapElement[];

  const findGeoConfig = (properties: any) => {
    const id = properties[mapDescriptors[currentPageMapBlock.mapType].key];

    const found = elements.find((element: any) => {
      return element.id === id;
    });
    return found;
  };

  const [tooltip, setTooltip] = useState<TooltipState>();
  const [selectedGeo, setSelectedGeo] = useState<CurrentMapElement>();
  const [isExiting, setIsExiting] = useState(false);

  const departmentFilter = currentPageMapBlock.departmentCode || '';

  const filterByDepartment = (g: any) => {
    const n = g.properties.dpto_ccdgo.toString().toLowerCase();
    return n === departmentFilter.toLowerCase();
  };

  const getGeographiesToFit = (geographies: any[]) => {
    if (currentPageMapBlock.mapType === MapType.Municipal) {
      return geographies.filter(filterByDepartment);
    }
    return geographies;
  };

  const { initialCenter, initialScale, fitGeographies } = useAutoFitGeoJson({
    height: currentPageMapBlock.height,
    padding: 40,
  });

  const [zoom, setZoom] = useState(currentPageMapBlock.initialZoom || initialScale);

  const handleClick = (
    e: React.MouseEvent<SVGPathElement, MouseEvent>,
    geo?: CurrentMapElement
  ) => {
    if (geo?.extra) {
      setSelectedGeo(geo);
      e.stopPropagation();
      return;
    }
    if (geo?.url) {
      open(geo.url, '_blank');
    }
    if (geo?.altName)
      setTooltip({
        name: geo.altName,
        x: e.clientX,
        y: e.clientY,
      });
  };

  const handleKeyBoardEvent = (e: React.KeyboardEvent<SVGPathElement>, geo?: CurrentMapElement) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (geo?.cardTitle) {
        setSelectedGeo(geo);
        e.stopPropagation();
        return;
      }
      if (geo?.url) {
        open(geo.url, '_blank');
      }
    }
  };

  const handleMouseEnter = (
    event: React.MouseEvent<SVGPathElement, MouseEvent>,
    geo: CurrentMapElement
  ) => {
    setTooltip({
      name: geo.altName,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseMove = (
    _event: React.MouseEvent<SVGPathElement, MouseEvent>,
    _geo: CurrentMapElement
  ) => {};

  const handleMouseLeave = () => {
    setTooltip(undefined);
  };

  const closeCard = () => {
    setIsExiting(true);
    setTimeout(() => {
      setSelectedGeo(undefined);
      setIsExiting(false);
    }, 300);
  };

  /**
   * Calcula el paso de desplazamiento basado en el nivel de zoom y la altura del mapa.
   *
   * Principio: El usuario debería poder atravesar el viewport visible con ~5 clics.
   * Esto balancea velocidad de navegación con precisión de posicionamiento.
   *
   * Fórmula: (altura del contenedor / 5) / zoom
   * - El divisor 5 representa los clics óptimos para atravesar el viewport
   * - Dividir por zoom manti<ene la distancia visual percibida constante
   *
   * Ejemplo con altura 400px:
   * - zoom 1x: paso = 80px → 5 clics atraviesan el mapa
   * - zoom 2x: paso = 40px → mismo esfuerzo percibido
   * - zoom 5x: paso = 16px → navegación fina proporcional
   */
  const CLICKS_TO_TRAVERSE = 5;
  const step = currentPageMapBlock.height / CLICKS_TO_TRAVERSE / zoom;

  const [translate, setTranslate] = useState([0, 0]);

  /**
   * Zoom discreto con niveles del 1 al 10.
   * Cada nivel representa un factor de escala entero.
   */
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 10;

  const zoomIn = () => {
    setZoom((prev) => Math.min(prev + 1, MAX_ZOOM));
  };
  const zoomOut = () => {
    setZoom((prev) => Math.max(prev - 1, MIN_ZOOM));
  };
  const moveLeft = () => {
    setTranslate([translate[0] - step, translate[1]]);
  };
  const moveRight = () => {
    setTranslate([translate[0] + step, translate[1]]);
  };
  const moveUp = () => {
    setTranslate([translate[0], translate[1] + step]);
  };
  const moveDown = () => {
    setTranslate([translate[0], translate[1] - step]);
  };
  const resetCenter = () => {
    setTranslate([0, 0]);
    setZoom(currentPageMapBlock.initialZoom || initialScale);
  };

  const geography = useMemo(() => {
    if (currentPageMapBlock.mapType === MapType.Custom) {
      try {
        if (currentPageMapBlock.geoJsonData) {
          const geoJsonFile = JSON.parse(currentPageMapBlock.geoJsonData);
          return JSON.parse(geoJsonFile.content);
        }
      } catch (e) {}
    }
    return mapDescriptors[currentPageMapBlock.mapType].file;
  }, [currentPageMapBlock.mapType]);

  return (
    <div>
      {currentPageMapBlock.showTitle && (
        <h3 className="mb-2 font-montserrat text-base font-semibold text-primary">
          {currentPageMapBlock.blockTitle}
        </h3>
      )}
      {currentPageMapBlock.showDescription && <p>{currentPageMapBlock.publicDescription}</p>}
      <div
        className="relative overflow-hidden rounded-xl border-2 border-[#BABABA] bg-[#FAFBFB]"
        style={{ height: currentPageMapBlock.height }}
      >
        <ComposableMap
          className="h-full w-full"
          style={{ transform: `scale(${zoom}) translate(${-translate[0]}px, ${translate[1]}px)` }}
          projection="geoMercator"
          projectionConfig={{
            scale: initialScale,
            center: initialCenter,
          }}
          onClick={(e) => {
            closeCard();
          }}
        >
          <Geographies geography={geography}>
            {({ geographies }) => {
              // delegate auto-centering to the hook
              fitGeographies(getGeographiesToFit(geographies));

              return geographies.map((geo) => {
                let highlight = true;
                if (currentPageMapBlock?.mapType === MapType.Municipal) {
                  highlight = geo.properties.dpto_ccdgo === currentPageMapBlock.departmentCode;
                }

                let callBackProps: Partial<
                  Pick<
                    GeographyProps,
                    'onMouseEnter' | 'onMouseMove' | 'onMouseLeave' | 'onClick' | 'onKeyDown'
                  >
                > = {};
                let geoStyles = getGeoStyles(false, undefined);

                if (highlight) {
                  const geoConfig = findGeoConfig(geo.properties);
                  if (geoConfig?.status === 'ACTIVE') {
                    callBackProps = {
                      onMouseEnter: highlight ? (e) => handleMouseEnter(e, geoConfig) : undefined,
                      onMouseMove: (e) => handleMouseMove(e, geoConfig),
                      onMouseLeave: handleMouseLeave,
                      onClick: (e) => handleClick(e, geoConfig),
                      onKeyDown: (e) => handleKeyBoardEvent(e, geoConfig),
                    };
                    geoStyles = getGeoStyles(highlight, geoConfig);
                  }
                }
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    {...callBackProps}
                    style={geoStyles}
                  />
                );
              });
            }}
          </Geographies>
        </ComposableMap>
        {selectedGeo?.cardTitle && (
          <div
            className={cx(
              'absolute right-4 top-4 z-10 max-w-sm duration-500 animate-in fade-in fade-out slide-in-from-right-5',
              isExiting
                ? 'translate-x-full opacity-0'
                : 'translate-x-0 opacity-100 animate-in fade-in slide-in-from-right-5'
            )}
          >
            <InfoCard
              title={selectedGeo.cardTitle}
              image={selectedGeo.cardImage?.at(0)?.downloadUrl || ''}
              description={selectedGeo.cardDescription || ''}
              buttonText={selectedGeo.cardButtonTitle || ''}
              buttonURL={selectedGeo.cardUrl || ''}
              maxHeight={currentPageMapBlock.height - 16}
            />
          </div>
        )}
        <div className="absolute left-0 top-0 p-2">
          <div className="flex flex-col items-center space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div></div>
              <button
                className="rounded bg-white p-2 shadow hover:bg-gray-100"
                onClick={moveUp}
                aria-label="Desplazar hacia arriba"
              >
                <ArrowUpIcon className="h-5 w-5" />
              </button>
              <div></div>

              <button
                className="rounded bg-white p-2 shadow hover:bg-gray-100"
                onClick={moveLeft}
                aria-label="Desplazar hacia la izquierda"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <button
                className="rounded bg-white p-2 shadow hover:bg-gray-100"
                onClick={resetCenter}
                aria-label="Centrar"
              >
                <TargetIcon className="h-5 w-5" />
              </button>
              <button
                className="rounded bg-white p-2 shadow hover:bg-gray-100"
                onClick={moveRight}
                aria-label="Desplazar hacia la derecha"
              >
                <ArrowRightIcon className="h-5 w-5" />
              </button>

              <div></div>
              <button
                className="rounded bg-white p-2 shadow hover:bg-gray-100"
                onClick={moveDown}
                aria-label="Desplazar hacia abajo"
              >
                <ArrowDownIcon className="h-5 w-5" />
              </button>
              <div></div>
            </div>

            <div className="flex space-x-2">
              <button
                className="rounded bg-white p-2 shadow hover:bg-gray-100"
                onClick={zoomOut}
                aria-label="Alejar zoom"
              >
                <MinusIcon className="h-5 w-5" />
              </button>
              <button
                className="rounded bg-white p-2 shadow hover:bg-gray-100"
                onClick={zoomIn}
                aria-label="Acercar zoom"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {tooltip && (
        <div
          className="pointer-events-none fixed z-50 rounded-lg bg-gray-900 px-4 py-2 text-white shadow-xl"
          style={{
            left: `${tooltip.x + 10}px`,
            top: `${tooltip.y + 10}px`,
            transform: 'translate(0, -50%)',
          }}
        >
          <p className="text-sm font-semibold">{tooltip.name}</p>
        </div>
      )}
    </div>
  );
};

export default MapBlockPresentation;

export function MapBlockPresentationSkeleton() {
  return (
    <div>
      <div className="mb-2 h-4 w-48 animate-pulse rounded bg-gray-200" />
      <div className="mb-4 h-3 w-72 animate-pulse rounded bg-gray-200" />
      <div
        className="relative overflow-hidden rounded-xl border-2 border-[#BABABA] bg-[#FAFBFB]"
        style={{ height: 300 }}
      >
        <div className="h-full w-full bg-gray-200" />
      </div>
    </div>
  );
}
